import React, { useEffect, useRef, useState } from 'react';
import {
   Tabs,
   useViewList,
   useDidUpdateEffect,
   useDidUpdateDeepCompareLayoutEffect,
   ToastList,
   useSelectGroup,
} from '@devesharp/react/web';
import dateFNS from 'date-fns';
import { FormHandles } from '@unform/core';
import { useRecoilState } from 'recoil';
import {
   APIServiceUrl,
   SubscriptionsManyUpdateResolve,
   SubscriptionsSearchResolve,
   GenerateExcel,
   GenerateExcelSubscriptions,
   GroupsSearchResolve,
   PaymentLinksSearchResolve,
   BoletoCancelResolve,
   SubscriptionsDeleteResolve,
   SplitSearchResolve, SplitDeleteResolve,
} from '~/api';
import { useUser } from '~/hooks';
import { companySelectState, companyShowInfoState } from '~/recoil/company-select.state';

export function useSplitListingPage(): any {
   document.title = 'Split - XPAG Tecnologia';

   const [showSeller] = useRecoilState(companyShowInfoState);

   const {
      starting,
      searching,
      errorLoadData,
      reloadPage,
      registerResolveParams,
      registerOnInit,
      resources,
      resourcesTotal,
      skeletonResources,
      setFilters,
      setOffset,
      setPage,
      setSort,
      filters,
   } = useViewList({
      resolves: {
         items: SplitSearchResolve,
      },
      filtersDefault: {},
   });

   const [onSelect, onUnselectAll, selected, checkSelected] = useSelectGroup();

   const [groups, setGroups] = useState<any[]>([]);
   const [loading, setLoading] = useState(false);
   const [currentPage, setCurrentPage] = useState(parseInt(((filters?.offset ?? 0) / 20) as any, 10) + 1);
   const formRef = useRef<FormHandles>();
   const formRefGroup = useRef<FormHandles>();

   registerOnInit((resources: any) => {
      if (resources.groups) {
         setGroups(resources.groups.results);
      }
   });

   /**
    * Seller Select
    */
   const user = useUser();
   const userRole = user.role;

   const [sellerSelect, setSellerSelect] = useRecoilState(companySelectState);
   useDidUpdateEffect(() => {
      reloadPage(true);
   }, [sellerSelect]);

   useDidUpdateEffect(() => {
      setPage(currentPage);
   }, [currentPage]);

   useEffect(() => {
      if (formRef.current) {
         formRef.current.setData(filters);
      }
      onUnselectAll();
      window.scroll({ top: 0, left: 0, behavior: 'smooth' });
   }, [filters]);

   const filtersData = {
      groups: [
         {
            id: '',
            name: 'Todos',
         },
         {
            id: -1,
            name: 'Sem grupo',
         },
         ...groups.map((group) => ({
            id: group.id,
            name: group.name,
         })),
      ],
      groupsChange: [
         {
            id: -1,
            name: 'Sem grupo',
         },
         ...groups.map((group) => ({
            id: group.id,
            name: group.name,
         })),
      ],
      status: [
         {
            id: '',
            name: 'Todos',
         },
         { id: 'succeeded', name: 'Pago' },
         { id: 'pending', name: 'Pendente' },
         { id: 'expired', name: 'Expirado' },
      ],
   };

   function generateExcel(): void {
      if (selected.size) {
         GenerateExcelSubscriptions({ ids: Array.from(selected.keys()) }).subscribe(
            (link) => {
               window.open(`${APIServiceUrl}/excel/${link}`);
            },
            (data) => {
               ToastList.show({
                  message: data.error,
                  type: 'error',
               });
            },
         );
      } else {
         GenerateExcelSubscriptions(filters).subscribe(
            (link) => {
               window.open(`${APIServiceUrl}/excel/${link}`);
            },
            (data) => {
               ToastList.show({
                  message: data.error,
                  type: 'error',
               });
            },
         );
      }
   }

   function onSubmit(data: any) {
      const params = data;
      params.created_at_gte = data?.created_at_gte?.dateYYYYMMDD;
      params.created_at_lte = data?.created_at_lte?.dateYYYYMMDD;

      setFilters(() => params);
   }

   const page = filters.page ?? 1;
   let pageCountStart = (page - 1) * 20 + 1;
   pageCountStart = resourcesTotal ? pageCountStart : 0;
   let pageCountEnd = (page - 1) * 20 + 20;
   pageCountEnd = pageCountEnd > resourcesTotal ? resourcesTotal : pageCountEnd;

   const [groupModalShow, setGroupModalShow] = useState(false);

   function changeSubscriptionGroup(): void {
      SubscriptionsManyUpdateResolve({
         ...formRefGroup.current?.getData(),
         ids: Array.from(selected.keys()),
      }).subscribe(
         () => {
            onUnselectAll();
            setGroupModalShow(false);
            reloadPage(false);
         },
         () => {
            // onUnselectAll();
            setGroupModalShow(false);
            ToastList.show({
               message: 'Erro ao mudar Split',
               type: 'error',
            });
         },
      );
   }

   function cancelSubscription(id: any) {
      const confirm = window.confirm(
         `Tem certeza que deseja cancelar essa assinatura? Essa ação não poderá ser desfeita.`,
      );

      if (!confirm) return;

      setLoading(true);
      SplitDeleteResolve(id).subscribe(
         () => {
            onUnselectAll();
            reloadPage(false);
            setLoading(false);
            ToastList.show({
               message: 'Split cancelada com sucesso!',
            });
         },
         () => {
            setLoading(false);
            ToastList.show({
               message: 'Erro ao cancelar assinatura',
               type: 'error',
            });
         },
      );
   }

   return {
      onSubmit,
      setSort,
      formRef,
      formRefGroup,
      changeSubscriptionGroup,
      groupModalShow,
      setGroupModalShow,
      reloadPage,
      starting,
      searching,
      loading,
      pageCountStart,
      pageCountEnd,
      errorLoadData,
      resources,
      resourcesTotal,
      skeletonResources,
      filters,
      generateExcel,
      filtersData,
      currentPage,
      setCurrentPage,
      onSelect,
      onUnselectAll,
      selected,
      checkSelected,
      userRole,
      showSeller,
      cancelSubscription,
   };
}
